import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import Icon from 'components/Icon';
import useControllerOperatorList from '../../Operator/OperatorList/_presenters/useControllerOperatorList/useControllerOperatorList';
import { useApiGet, useApiPost } from '../../../hooks/useApi';
import { Spinner } from 'reactstrap';
import Button from 'components/Button';

type RechargePayload = {
  phone: {
    area_code: string;
    number: string;
  };
  operator_code: string;
  product_code?: string;
  value: number;
};

type RechargeResponse = {
  id: string;
  transaction_id: string;
  phone_number: string;
  operator_code: string;
  value: number;
  status: string;
  pix: {
    txid: string;
    pix_copia_e_cola: string;
    status: string;
  };
};

const RechargeDetailsPage = () => {
  const [recharge, setRecharge] = useState<RechargeResponse>();
  const [searchParams] = useSearchParams();
  const ddd = searchParams.get('ddd');
  const phone = searchParams.get('phone');
  const value = searchParams.get('value');
  const navigate = useNavigate();
  const [timeLeft, setTimeLeft] = useState<number>(120); // 2 minutos em segundos

  const { operadoras } = useControllerOperatorList({
    ddd: ddd
  });

  const operadora = useMemo(() => {
    const operatorCode = searchParams.get('operator');
    return operadoras?.find((operator: any) => operator.code === operatorCode);
  }, [operadoras, searchParams]);

  const { mutateAsync, isPending, isError } = useApiPost<
    RechargeResponse,
    RechargePayload
  >('/recharge/recharges');

  const { data: result } = useApiGet('/recharge/recharges', {
    id: recharge?.id,
    enabled: !!recharge,
    refetchInterval: 1000
  });

  const gerarPagamento = useCallback(async () => {
    setTimeLeft(120);
    const ddd = searchParams.get('ddd');
    const phone = searchParams.get('phone');
    const value = Number(searchParams.get('value') || 0);
    const operatorCode = searchParams.get('operator');
    if (ddd && phone && value && operatorCode) {
      const response = await mutateAsync({
        phone: {
          area_code: ddd,
          number: phone
        },
        operator_code: operatorCode,
        value
      });
      setRecharge(response);
    }
  }, [mutateAsync, searchParams]);

  useEffect(() => {
    if (recharge) return;
    gerarPagamento();
  }, [gerarPagamento, recharge]);

  useEffect(() => {
    if (result?.paid) {
      navigate('/recarga/compra-realizada');
    }
  }, [navigate, result]);

  // Timer for payment countdown
  useEffect(() => {
    if (!recharge) return;

    const intervalId = setInterval(() => {
      setTimeLeft((prevTime) => {
        if (prevTime <= 1) {
          clearInterval(intervalId);
          return 0;
        }
        return prevTime - 1;
      });
    }, 1000);

    return () => clearInterval(intervalId);
  }, [recharge]);

  return (
    <>
      <Link
        className="back-button"
        to={`/recarga?ddd=${ddd}&phone=${phone}&operator=${searchParams.get(
          'operator'
        )}`}
      >
        <Icon icon="chevron-left" width={64} height={64} />
        Voltar
      </Link>
      <div className="details-recharge">
        <p className="font-size-3">Você está carregando</p>
        <p className="font-size-4">Atenção! Confirme a sua compra</p>
        <div className="details-recharge-info">
          <p className="font-size-4 pb-0">
            <span className="font-size-4">Número - </span>{' '}
            <span className="font-size-5">
              ({ddd}) {phone?.substring(0, 5)}-{phone?.substring(5)}
            </span>
          </p>
          <p>
            <span className="font-size-4">Operadora - </span>{' '}
            <span className="font-size-5">{operadora?.name}</span>
          </p>
          <p>
            <span className="font-size-4">Valor - </span>{' '}
            <span className="font-size-5">
              R${' '}
              {Number(value || 0)
                .toFixed(2)
                .toLocaleString()}
            </span>
          </p>
        </div>
        <hr className="details-recharge-border" />
        {isPending ? (
          <div className="d-flex justify-content-center align-items-center flex-column">
            <Spinner />
            <p>Gerando QR Code do Pix, aguarde...</p>
          </div>
        ) : (
          <>
            {isError && (
              <div className="d-flex justify-content-center align-items-center flex-column">
                <p>
                  Não foi possível gerar o QRCode, por favor verifique se o
                  número de telefone e a operadora estão corretos e tente
                  novamente!
                </p>
                <Button
                  color="primary"
                  className="text-white btn-lg rounded-3 mb-3"
                  loading={isPending}
                  onClick={gerarPagamento}
                >
                  Tentar novamente!
                </Button>
              </div>
            )}
            {recharge && (
              <>
                {timeLeft > 0 ? (
                  <>
                    <div className="d-flex justify-content-center align-items-center mb-3 flex-column">
                      <div className="bg-light p-3 rounded-circle">
                        <span className="font-size-5 fw-bold text-danger">
                          {Math.floor(timeLeft / 60)}:
                          {(timeLeft % 60).toString().padStart(2, '0')}
                        </span>
                      </div>
                      <p>Use o QRCode abaixo para efetuar o pagamento</p>
                    </div>
                    <QRCodeCanvas
                      value={recharge.pix.pix_copia_e_cola}
                      size={200}
                      className="my-3"
                    />
                    <p className="font-size-4 detais-recharge-text">
                      Acesse o app do seu banco e faça a leitura do código QR
                      acima. Quando a operação for concluída, uma nova tela irá
                      carregar automaticamente. Você tem{' '}
                      <strong>2 minutos</strong> para concluir o pagamento.{' '}
                    </p>
                  </>
                ) : (
                  <>
                    <div className="d-flex justify-content-center align-items-center mb-3 flex-column">
                      <div className="bg-light p-3 rounded-circle">
                        <span className="font-size-5 fw-bold text-danger">
                          00:00
                        </span>
                      </div>
                      <p>Pix expirado!</p>
                    </div>
                    <Button
                      color="primary"
                      className="text-white btn-lg rounded-3 mb-3"
                      loading={isPending}
                      onClick={gerarPagamento}
                    >
                      Gerar novo pix
                    </Button>
                  </>
                )}
              </>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default RechargeDetailsPage;
